import { FormEvent, useEffect, useState } from "react";
import type { ReactElement } from "react";

type Route = "home" | "impressum" | "datenschutz";

type ContactStatus =
  | { kind: "idle"; message: string }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string }
  | { kind: "loading"; message: string };

type TurnstileWindow = Window & {
  onCytrackTurnstileSuccess?: (token: string) => void;
  onCytrackTurnstileExpired?: () => void;
};

const turnstileSiteKey = String(import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "");

const features = [
  {
    title: "JS-gerenderte Snapshots",
    text: "Playwright rendert moderne SPAs, wartet auf Netzwerkruhe, scrollt optional automatisch und sichert den finalen DOM-Zustand."
  },
  {
    title: "Revisionssichere Nachweise",
    text: "Jeder Job erzeugt Audit-Logs, SHA-256-Hashes, Manifestdaten und Reports, damit spätere Änderungen erkennbar sind."
  },
  {
    title: "Offline-Browsing",
    text: "CYTrack speichert HTML, CSS, Bilder, Assets und interne Links so, dass archivierte Seiten lokal geprüft werden können."
  },
  {
    title: "Sichere Defaults",
    text: "Robots.txt, Scope-Grenzen, Rate-Limits, SSRF-Schutz, Preview-Isolation und Lizenz-Gates sind produktnah integriert."
  }
];

const workflows = [
  "Website mit Wizard anlegen",
  "Scope, Tiefe und Rate-Limits prüfen",
  "Mirror-Job starten und live überwachen",
  "Audit-Bundle, Report oder Offline-Paket exportieren"
];

const audiences = [
  "Agenturen und Betreiber, die Kunden-Websites dokumentieren müssen",
  "Migrationsteams, die Ist-Zustände beweissicher festhalten",
  "Compliance- und QA-Teams mit revisionsfähigen Web-Archiven",
  "Self-Hosted-Admins, die keine Public-SaaS-Plattform nutzen wollen"
];

function currentRoute(): Route {
  if (window.location.pathname.startsWith("/impressum")) return "impressum";
  if (window.location.pathname.startsWith("/datenschutz")) return "datenschutz";
  return "home";
}

export function App(): ReactElement {
  const [route, setRoute] = useState<Route>(currentRoute);

  useEffect(() => {
    const onPopState = (): void => setRoute(currentRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function navigate(path: string): void {
    window.history.pushState({}, "", path);
    setRoute(currentRoute());
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="site-shell">
      <Header navigate={navigate} />
      {route === "home" && <HomePage />}
      {route === "impressum" && <LegalPage title="Impressum" kind="impressum" />}
      {route === "datenschutz" && <LegalPage title="Datenschutzerklärung" kind="datenschutz" />}
      <Footer navigate={navigate} />
    </div>
  );
}

function Header({ navigate }: { navigate: (path: string) => void }): ReactElement {
  return (
    <header className="nav">
      <button className="brand" onClick={() => navigate("/")} aria-label="Zur CYTrack Startseite">
        <span className="brand-mark">CY</span>
        <span>
          <strong>CYTrack</strong>
          <small>Website-Archivierung mit Audit-Nachweis</small>
        </span>
      </button>
      <nav aria-label="Hauptnavigation">
        <a href="#funktionen">Funktionen</a>
        <a href="#workflows">Workflow</a>
        <a href="#sicherheit">Sicherheit</a>
        <a href="#kontakt" className="nav-cta">
          Anfrage senden
        </a>
      </nav>
    </header>
  );
}

function HomePage(): ReactElement {
  return (
    <main>
      <section className="hero">
        <div className="hero-copy">
          <span className="eyebrow">Self-hosted Mirror-Plattform</span>
          <h1>Moderne Websites sichern. Gerendert, offline nutzbar und auditierbar.</h1>
          <p>
            CYTrack archiviert klassische Websites und JavaScript-Apps mit lokaler Engine, Desktop-App,
            CLI und Web-Dashboard. Gebaut für autorisierte Sicherungen, Migrationen, QS und
            revisionsfähige Dokumentation.
          </p>
          <div className="hero-actions">
            <a href="#kontakt" className="button button-primary">
              Demo anfragen
            </a>
            <a href="#funktionen" className="button button-secondary">
              Funktionen ansehen
            </a>
          </div>
        </div>
        <div className="hero-card" aria-label="CYTrack Produktvorschau">
          <div className="window-bar">
            <span />
            <span />
            <span />
          </div>
          <div className="dashboard-preview">
            <div>
              <small>Aktiver Mirror</small>
              <strong>cytrack-demo.de</strong>
            </div>
            <div className="progress-ring">86%</div>
            <div className="metric-grid">
              <span>
                <b>128</b>
                Seiten
              </span>
              <span>
                <b>2.418</b>
                Assets
              </span>
              <span>
                <b>SHA-256</b>
                Manifest
              </span>
              <span>
                <b>Live</b>
                Audit-Log
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="logo-strip" aria-label="Betriebsarten">
        <span>CLI</span>
        <span>Web-Dashboard</span>
        <span>Desktop-App</span>
        <span>Debian LXC/VM</span>
        <span>Cloudflare Tunnel Ready</span>
      </section>

      <section id="funktionen" className="section">
        <div className="section-heading">
          <span className="eyebrow">Funktionen</span>
          <h2>Ein Archivierungs-Workflow statt loser Screenshots.</h2>
          <p>
            CYTrack verbindet Rendering, Crawling, Link-Rewriting, Hash-Nachweise und Exporte in einem
            Werkzeug.
          </p>
        </div>
        <div className="feature-grid">
          {features.map((feature) => (
            <article className="feature-card" key={feature.title}>
              <span className="feature-index">{feature.title.slice(0, 2).toUpperCase()}</span>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="workflows" className="split-section">
        <div>
          <span className="eyebrow">Ablauf</span>
          <h2>Von der URL zum auditierbaren Export.</h2>
          <p>
            Die Oberfläche führt Nutzer durch sichere Voreinstellungen. Für Automatisierung stehen CLI
            und API bereit.
          </p>
        </div>
        <ol className="timeline">
          {workflows.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>

      <section id="sicherheit" className="section security-panel">
        <div className="section-heading">
          <span className="eyebrow">Sicherheit und Compliance</span>
          <h2>Defensiv gebaut für berechtigte Sicherungen.</h2>
        </div>
        <div className="security-grid">
          <p>Robots.txt wird standardmäßig respektiert.</p>
          <p>Remote-Betrieb ist für Cloudflare Access oder Reverse-Proxy-Auth ausgelegt.</p>
          <p>Mirror-Vorschauen werden restriktiv isoliert, damit archivierte Seiten keine Admin-API nutzen.</p>
          <p>Keine Funktionen zum Umgehen von Paywalls, Captchas, DRM oder Zugriffssperren.</p>
        </div>
      </section>

      <section className="split-section">
        <div>
          <span className="eyebrow">Für wen?</span>
          <h2>CYTrack passt zu Teams, die Nachweise brauchen.</h2>
        </div>
        <ul className="audience-list">
          {audiences.map((audience) => (
            <li key={audience}>{audience}</li>
          ))}
        </ul>
      </section>

      <ContactSection />
    </main>
  );
}

function ContactSection(): ReactElement {
  const [status, setStatus] = useState<ContactStatus>({
    kind: "idle",
    message: "Ihre Anfrage wird mit Cloudflare Turnstile vor Spam geschützt."
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const siteKey = turnstileSiteKey || "1x00000000000000000000AA";

  useEffect(() => {
    const turnstileWindow = window as TurnstileWindow;
    turnstileWindow.onCytrackTurnstileSuccess = (token: string): void => setTurnstileToken(token);
    turnstileWindow.onCytrackTurnstileExpired = (): void => setTurnstileToken("");
    return () => {
      delete turnstileWindow.onCytrackTurnstileSuccess;
      delete turnstileWindow.onCytrackTurnstileExpired;
    };
  }, []);

  async function submitContact(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = {
      name: String(formData.get("name") ?? ""),
      email: String(formData.get("email") ?? ""),
      company: String(formData.get("company") ?? ""),
      message: String(formData.get("message") ?? ""),
      companyWebsite: String(formData.get("companyWebsite") ?? ""),
      turnstileToken
    };

    setStatus({ kind: "loading", message: "Anfrage wird geprüft und gesendet..." });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload)
      });
      const result = (await response.json()) as { message?: string };
      if (!response.ok) {
        throw new Error(result.message ?? "Die Anfrage konnte nicht gesendet werden.");
      }
      form.reset();
      setTurnstileToken("");
      setStatus({
        kind: "success",
        message: result.message ?? "Danke. Wir melden uns zeitnah zur CYTrack-Demo."
      });
    } catch (error) {
      setStatus({
        kind: "error",
        message: error instanceof Error ? error.message : "Die Anfrage konnte nicht gesendet werden."
      });
    }
  }

  return (
    <section id="kontakt" className="contact-section">
      <div>
        <span className="eyebrow">Kontakt</span>
        <h2>CYTrack testen oder ein Angebot anfragen.</h2>
        <p>
          Beschreiben Sie kurz den geplanten Einsatz. Wir melden uns mit Deployment-Vorschlag,
          Lizenzmodell und Testzugang.
        </p>
        <div className="contact-facts">
          <span>hello@cyblinge.de</span>
          <span>+49 (0)8549 3069826</span>
          <span>Vilshofen a.d. Donau</span>
        </div>
      </div>
      <form className="contact-form" onSubmit={(event) => void submitContact(event)}>
        <label>
          Name
          <input name="name" autoComplete="name" required minLength={2} maxLength={120} />
        </label>
        <label>
          E-Mail
          <input name="email" type="email" autoComplete="email" required maxLength={180} />
        </label>
        <label>
          Firma
          <input name="company" autoComplete="organization" maxLength={160} />
        </label>
        <label>
          Anfrage
          <textarea name="message" required minLength={20} maxLength={2500} rows={6} />
        </label>
        <label className="honeypot" aria-hidden="true">
          Website
          <input name="companyWebsite" tabIndex={-1} autoComplete="off" />
        </label>
        <div
          className="cf-turnstile"
          data-sitekey={siteKey}
          data-callback="onCytrackTurnstileSuccess"
          data-expired-callback="onCytrackTurnstileExpired"
          data-theme="light"
        />
        {!turnstileSiteKey && (
          <p className="form-note">
            Demo-Site-Key aktiv. Für Cloudflare Pages bitte `VITE_TURNSTILE_SITE_KEY` und
            `TURNSTILE_SECRET_KEY` setzen.
          </p>
        )}
        <button className="button button-primary" type="submit" disabled={status.kind === "loading"}>
          Anfrage senden
        </button>
        <p className={`form-status form-status-${status.kind}`} role="status">
          {status.message}
        </p>
      </form>
    </section>
  );
}

function LegalPage({ title, kind }: { title: string; kind: "impressum" | "datenschutz" }): ReactElement {
  return (
    <main className="legal-page">
      <section className="legal-card">
        <span className="eyebrow">CYTrack Marketing Website</span>
        <h1>{title}</h1>
        {kind === "impressum" ? <ImpressumContent /> : <PrivacyContent />}
      </section>
    </main>
  );
}

function ImpressumContent(): ReactElement {
  return (
    <div className="legal-content">
      <h2>Angaben gemäß § 5 TMG</h2>
      <p>
        CYBLINGE UG (haftungsbeschränkt)
        <br />
        Am Plattel 8
        <br />
        94474 Vilshofen
        <br />
        Deutschland
      </p>
      <h2>Kontakt</h2>
      <p>
        Telefon: +49 (0)8549 3069826
        <br />
        E-Mail: hello@cyblinge.de
        <br />
        Website: www.cyblinge.de
      </p>
      <h2>Vertreten durch</h2>
      <p>Geschäftsführer: Marius Bleek</p>
      <h2>Registereintrag</h2>
      <p>
        Eintragung im Handelsregister
        <br />
        Registergericht: Amtsgericht Passau
        <br />
        Registernummer: HRB wird nachgereicht
      </p>
      <h2>Umsatzsteuer-ID</h2>
      <p>Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz: wird nachgereicht</p>
      <h2>Verantwortlich für den Inhalt</h2>
      <p>
        Marius Bleek
        <br />
        Am Plattel 8
        <br />
        94474 Vilshofen a.d. Donau
      </p>
      <p className="legal-note">
        Hinweis: Diese Angaben wurden aus der öffentlichen CYBLINGE-Impressumsseite übernommen und
        sollten vor Veröffentlichung der CYTrack-Seite final rechtlich geprüft werden.
      </p>
    </div>
  );
}

function PrivacyContent(): ReactElement {
  return (
    <div className="legal-content">
      <h2>Verantwortliche Stelle</h2>
      <p>
        CYBLINGE UG (haftungsbeschränkt), Am Plattel 8, 94474 Vilshofen a.d. Donau,
        hello@cyblinge.de.
      </p>
      <h2>Hosting über Cloudflare Pages</h2>
      <p>
        Diese Marketing-Website ist für Cloudflare Pages vorgesehen. Beim Aufruf verarbeitet
        Cloudflare technische Zugriffsdaten wie IP-Adresse, Zeitpunkt, Browserinformationen und
        angefragte Ressourcen, um die Website sicher und performant bereitzustellen.
      </p>
      <h2>Kontaktformular</h2>
      <p>
        Wenn Sie das Kontaktformular nutzen, verarbeiten wir Name, E-Mail-Adresse, Firma,
        Nachrichteninhalt und technische Zustelldaten zur Bearbeitung Ihrer Anfrage. Grundlage ist
        Art. 6 Abs. 1 lit. b DSGVO, wenn die Anfrage vorvertraglich oder vertraglich ist, sonst Art.
        6 Abs. 1 lit. f DSGVO.
      </p>
      <h2>Spam-Schutz mit Cloudflare Turnstile</h2>
      <p>
        Das Formular nutzt Cloudflare Turnstile. Turnstile hilft, automatisierte Spam-Anfragen zu
        erkennen. Dabei können technische Daten an Cloudflare übermittelt werden. Die Verarbeitung
        erfolgt auf Grundlage unseres berechtigten Interesses an einer sicheren und spamarmen
        Kontaktaufnahme gemäß Art. 6 Abs. 1 lit. f DSGVO.
      </p>
      <h2>Ihre Rechte</h2>
      <p>
        Sie haben nach Maßgabe der DSGVO insbesondere Rechte auf Auskunft, Berichtigung, Löschung,
        Einschränkung der Verarbeitung, Datenübertragbarkeit, Widerspruch und Beschwerde bei einer
        zuständigen Aufsichtsbehörde.
      </p>
      <h2>Speicherdauer</h2>
      <p>
        Kontaktanfragen werden nur so lange gespeichert, wie dies für Bearbeitung, Nachweis oder
        gesetzliche Pflichten erforderlich ist.
      </p>
      <p className="legal-note">
        Hinweis: Diese Datenschutzhinweise sind auf die CYTrack-Marketing-Seite zugeschnitten und
        ersetzen keine finale juristische Prüfung vor Livegang.
      </p>
    </div>
  );
}

function Footer({ navigate }: { navigate: (path: string) => void }): ReactElement {
  return (
    <footer className="footer">
      <div>
        <strong>CYTrack</strong>
        <p>Kommerzielle Software von CYBLINGE für autorisierte Website-Archivierung.</p>
      </div>
      <div className="footer-links">
        <button onClick={() => navigate("/impressum")}>Impressum</button>
        <button onClick={() => navigate("/datenschutz")}>Datenschutz</button>
        <a href="mailto:hello@cyblinge.de">Kontakt</a>
      </div>
    </footer>
  );
}
